import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Phone, ArrowUpRight, ArrowLeft, Check, ChevronDown, ShieldCheck, Wrench, Wind, Home as HomeIcon, SearchCheck, Waves, Clock } from "lucide-react";
import { COMPANY, SERVICES, AREAS, BBB, HOURS } from "@/content";
import CallLink from "@/components/CallLink";
import { Reveal } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const ICONS = { maintenance: Wrench, repair: Wind, inspections: SearchCheck, "duct-cleaning": Waves, "indoor-air-quality": HomeIcon };

function useServiceSchema(s) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "service-schema";
    el.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: s.title,
          description: s.seoDescription,
          serviceType: s.title,
          provider: { "@type": "HVACBusiness", name: `${COMPANY.full}, LLC`, telephone: "+1-239-671-4867", url: window.location.origin },
          areaServed: AREAS.map((a) => ({ "@type": "City", name: a })),
          url: window.location.href,
          image: s.image,
        },
        { "@type": "FAQPage", mainEntity: s.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: window.location.origin + "/" },
            { "@type": "ListItem", position: 2, name: "Services", item: window.location.origin + "/services" },
            { "@type": "ListItem", position: 3, name: s.title, item: window.location.href },
          ],
        },
      ],
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [s]);
}

function FAQ({ items, slug }) {
  return (
    <div className="divide-y divide-sand rounded-3xl border border-sand bg-white overflow-hidden">
      {items.map((f, i) => (
        <details key={f.q} data-testid={`faq-${slug}-${i}`} className="group px-6 md:px-8">
          <summary className="flex cursor-pointer items-center justify-between gap-6 py-5 md:py-6 font-medium text-charcoal list-none [&::-webkit-details-marker]:hidden">
            <span className="text-base md:text-lg">{f.q}</span>
            <ChevronDown size={18} className="shrink-0 text-terracotta transition-transform duration-300 group-open:rotate-180" />
          </summary>
          <p className="pb-6 -mt-1 text-ink leading-relaxed">{f.a}</p>
        </details>
      ))}
    </div>
  );
}

export default function ServiceDetail() {
  const { path } = useParams();
  const s = SERVICES.find((x) => x.path === path);
  usePageMeta(s?.seoTitle, s?.seoDescription);
  useServiceSchema(s || SERVICES[0]);
  if (!s) return <Navigate to="/services" replace />;

  const Icon = ICONS[s.slug] || ShieldCheck;
  const idx = SERVICES.indexOf(s);
  const others = SERVICES.filter((x) => x.path !== path);

  return (
    <>
      <section data-testid={`service-page-${s.slug}`} className="relative pt-36 pb-16 md:pt-48 md:pb-24 overflow-hidden bg-charcoal text-bone">
        <div className="absolute inset-0 z-0">
          <img src={s.image} alt={s.title} className="h-full w-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/85 to-charcoal/30" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-10">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs md:text-sm text-bone/60 mb-8">
            <Link to="/" className="hover:text-bone transition-colors">Home</Link><span>/</span>
            <Link to="/services" className="hover:text-bone transition-colors">Services</Link><span>/</span>
            <span className="text-bone">{s.title}</span>
          </nav>
          <div className="flex items-center gap-3 mb-5">
            <span className="grid place-items-center h-11 w-11 rounded-full bg-terracotta text-bone"><Icon size={20} /></span>
            <p className="text-terracotta text-xs md:text-sm tracking-[0.3em] uppercase font-semibold">Service 0{idx + 1} · {AREAS.join(" · ")}</p>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95] max-w-3xl">{s.title}</h1>
          <p className="mt-5 max-w-2xl text-bone/85 text-lg md:text-xl leading-relaxed font-heading">{s.headline}</p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <a href="#quote" data-testid={`service-page-cta-${s.slug}`} className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-7 py-3.5 font-semibold text-bone hover:bg-terracotta-dark hover:-translate-y-0.5 transition-all duration-200">
              Request this service <ArrowUpRight size={17} />
            </a>
            <CallLink className="inline-flex items-center justify-center gap-2 rounded-full border border-bone/40 bg-bone/5 backdrop-blur px-7 py-3.5 font-semibold text-bone hover:bg-bone/15 transition-colors duration-200">
              <Phone size={16} /> {COMPANY.phone}
            </CallLink>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-20 items-start">
        <Reveal>
          <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-4">About this service</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight text-charcoal leading-[1]">{s.short}</h2>
          <p className="mt-6 text-ink text-lg leading-relaxed">{s.body}</p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {s.points.map((p) => (
              <li key={p} className="flex items-start gap-3 rounded-2xl bg-white border border-sand p-4">
                <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-sage/15 text-sage shrink-0"><Check size={14} /></span>
                <span className="text-charcoal">{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="rounded-3xl overflow-hidden">
            <img src={s.image} alt={s.title} className="w-full h-[320px] md:h-[420px] object-cover" />
          </div>
          <div className="mt-5 rounded-2xl bg-sage text-bone p-6 flex items-center gap-4">
            <img src={BBB.seal} alt="BBB Accredited Business" className="h-12 w-auto rounded bg-white p-1" />
            <div className="leading-tight">
              <p className="font-heading text-xl">{BBB.rating} BBB Rated · Licensed {BBB.license}</p>
              <p className="text-sm text-bone/75">Veteran-owned · 10% off for veterans &amp; first responders</p>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-sand/50 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-4">What to expect</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight text-charcoal leading-[1] max-w-2xl">Simple, honest, start to finish.</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {s.steps.map((st, i) => (
              <Reveal key={st.t} delay={i * 0.08}>
                <div className="h-full rounded-2xl bg-white border border-sand p-7">
                  <span className="font-heading text-5xl text-terracotta/70">0{i + 1}</span>
                  <h3 className="mt-3 font-heading text-2xl text-charcoal">{st.t}</h3>
                  <p className="mt-2 text-ink leading-relaxed">{st.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-16 items-start">
        <Reveal>
          <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-4">Common questions</p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl tracking-tight text-charcoal leading-[1]">{s.title} FAQs</h2>
          <p className="mt-5 text-ink leading-relaxed">Have a question we didn't cover? Call us — we're happy to talk it through, no sales pitch.</p>
          <CallLink className="mt-6 inline-flex items-center gap-2 text-terracotta font-semibold hover:underline"><Phone size={16} /> {COMPANY.phone}</CallLink>
        </Reveal>
        <Reveal delay={0.1}><FAQ items={s.faqs} slug={s.slug} /></Reveal>
      </section>

      <section className="bg-charcoal text-bone py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
              <h2 className="font-heading text-3xl md:text-4xl tracking-tight leading-[1]">Other services we offer</h2>
              <Link to="/services" className="inline-flex items-center gap-2 text-bone/80 hover:text-bone font-semibold transition-colors">View all services <ArrowUpRight size={17} /></Link>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {others.map((o, i) => {
              const OIcon = ICONS[o.slug] || ShieldCheck;
              return (
                <Reveal key={o.path} delay={i * 0.06}>
                  <Link to={`/services/${o.path}`} data-testid={`related-service-${o.slug}`} className="group block h-full rounded-2xl border border-bone/15 bg-bone/5 p-6 hover:bg-bone/10 hover:-translate-y-1 transition-all duration-300">
                    <span className="grid place-items-center h-10 w-10 rounded-full bg-terracotta text-bone"><OIcon size={18} /></span>
                    <h3 className="mt-4 font-heading text-xl">{o.title}</h3>
                    <p className="mt-1.5 text-sm text-bone/70 leading-relaxed">{o.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-terracotta font-semibold text-sm group-hover:gap-2.5 transition-all">Learn more <ArrowUpRight size={14} /></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section id="quote" className="bg-sand/50 py-24 md:py-32">
        <div className="mx-auto max-w-6xl px-5 md:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <Reveal>
            <p className="text-terracotta text-xs tracking-[0.25em] uppercase font-semibold mb-5">Get started</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-charcoal leading-[0.98]">Request this service.</h2>
            <p className="mt-6 text-ink leading-relaxed max-w-md">Tell us a little about your system and we'll follow up quickly with honest advice and a free quote for {s.title.toLowerCase().replace(/a\/c/g, "A/C")}.</p>
            <CallLink className="mt-8 inline-flex items-center gap-3 rounded-2xl bg-sage text-bone px-6 py-5 hover:bg-sage/90 transition-colors duration-200">
              <span className="grid place-items-center h-11 w-11 rounded-full bg-bone/15"><Phone size={20} /></span>
              <span><span className="block text-xs uppercase tracking-widest text-bone/70">Call us today</span><span className="font-heading text-2xl">{COMPANY.phone}</span></span>
            </CallLink>
            <div data-testid="business-hours" className="mt-4 flex w-fit items-center gap-2.5 rounded-full border border-sand bg-white px-4 py-2 text-sm text-charcoal">
              <Clock size={15} className="text-terracotta" />
              <span><span className="font-semibold">{HOURS.days}</span> · {HOURS.time}</span>
            </div>
            <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-terracotta font-semibold hover:underline"><ArrowLeft size={16} /> All services</Link>
          </Reveal>
          <Reveal delay={0.1}><ContactForm defaultService={s.title} /></Reveal>
        </div>
      </section>
    </>
  );
}
